import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SysTablesService } from 'app/core/services/sys-tables.service';
import { SettingsHttpService } from 'app/features/settings/services/settings.service';
import { catchError, exhaustMap, forkJoin, from, map, mergeMap, of } from 'rxjs';
import { Aircraft } from 'app/features/settings/models/aircraft.model';
import { SysAircraftMake } from 'app/core/models/sys-aircraft-make.model';
import { SysAircraftModel } from 'app/core/models/sys-aircraft-model.model';
import { AIRCRAFT_ACTIONS } from '.';
import { sortByStringProperty } from 'app/core/utils/sorting.utils';
import { SysApuModel } from 'app/features/settings/models/sys-apu-model.model';

@Injectable()
export class AircraftEffects {
  loadAllAircrafts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIRCRAFT_ACTIONS.triggerAllAircrafts),
      mergeMap(() =>
        forkJoin([
          this.settingsService.getAircrafts(),
          this.sysTablesService.getSysAircraftModels(),
          this.sysTablesService.getSysAircraftMakes(),
        ]).pipe(
          mergeMap(([aircrafts, aircraftModels, aircraftMakes]) => {
            aircrafts = (aircrafts ?? []).map(aircraft =>
              this._getUpdatedAircraftName(aircraft, aircraftModels, aircraftMakes)
            );
            aircrafts = sortByStringProperty<Aircraft>(aircrafts, 'serialNumber');

            aircraftModels = (aircraftModels ?? []).filter(model => model.isActive);
            aircraftModels = sortByStringProperty<SysAircraftModel>(aircraftModels, 'nameEn');

            aircraftMakes = (aircraftMakes ?? []).filter(make => make.isActive);
            aircraftMakes = sortByStringProperty<SysAircraftMake>(aircraftMakes, 'nameEn');

            // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
            return from([
              AIRCRAFT_ACTIONS.aircraftsSuccess({ aircrafts }),
              AIRCRAFT_ACTIONS.aircraftModelsSuccess({ aircraftModels }),
              AIRCRAFT_ACTIONS.aircraftMakesSuccess({ aircraftMakes }),
            ]);
          })
        )
      )
    );
  });

  loadAircraft$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIRCRAFT_ACTIONS.triggerAircrafts),
      exhaustMap(() =>
        this.settingsService.getAircrafts().pipe(
          map((aircrafts: Aircraft[]) => AIRCRAFT_ACTIONS.aircraftsSuccess({ aircrafts })),
          catchError(error => of(AIRCRAFT_ACTIONS.aircraftsFailure({ error: error.message })))
        )
      )
    );
  });

  loadAircraftMakes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIRCRAFT_ACTIONS.triggerAircraftMakes),
      exhaustMap(() =>
        this.sysTablesService.getSysAircraftMakes().pipe(
          map((aircraftMakes: SysAircraftMake[]) =>
            AIRCRAFT_ACTIONS.aircraftMakesSuccess({ aircraftMakes })
          ),
          catchError(error => of(AIRCRAFT_ACTIONS.aircraftMakesFailure({ error: error.message })))
        )
      )
    );
  });

  loadAircraftModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIRCRAFT_ACTIONS.triggerAircraftModels),
      exhaustMap(() =>
        this.sysTablesService.getSysAircraftModels().pipe(
          map((aircraftModels: SysAircraftModel[]) =>
            AIRCRAFT_ACTIONS.aircraftModelsSuccess({ aircraftModels })
          ),
          catchError(error => of(AIRCRAFT_ACTIONS.aircraftModelsFailure({ error: error.message })))
        )
      )
    );
  });

  loadApuModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIRCRAFT_ACTIONS.triggerApuModels),
      exhaustMap(() =>
        this.sysTablesService.getSysApuModels().pipe(
          map((apuModels: SysApuModel[]) => AIRCRAFT_ACTIONS.apuModelsSuccess({ apuModels })),
          catchError(error => of(AIRCRAFT_ACTIONS.apuModelsFailure({ error: error.message })))
        )
      )
    );
  });

  private _getUpdatedAircraftName(
    aircraft: Aircraft,
    aircraftModels: SysAircraftModel[],
    aircraftMakes: SysAircraftMake[]
  ): Aircraft {
    const aircraftModel = aircraftModels.find(model => model.id === aircraft?.sysAircraftModelId);
    const aircraftMake = aircraftMakes.find(make => make.id === aircraftModel?.sysAircraftMakeId);
    return {
      ...aircraft,
      sysAircraftModelName: `${aircraft.serialNumber} - ${aircraftMake?.nameEn} ${aircraftModel?.nameEn}`,
    };
  }

  constructor(
    private actions$: Actions,
    private sysTablesService: SysTablesService,
    private settingsService: SettingsHttpService
  ) {}
}
